/**
 * 미션 제출 페이지 — 미션 가이드 섹션
 *
 * 현재 차수의 가이드라인 텍스트와 첨부파일을 표시한다.
 * 모바일에서는 긴 가이드 텍스트를 접힘 처리한다.
 */

import { useEffect, useRef, useState } from 'react'
import { FileText } from 'lucide-react'
import { Linkify } from '@/components/Linkify'
import type { PublicCampaignResponse, MissionRound } from '@/lib/seedingPublicApi'
import { extractFilename, formatDate } from '@/features/apply/components/utils'
import type { RoundSubmissionStatus } from './RoundTabs'

interface MissionGuideSectionProps {
  campaign: PublicCampaignResponse
  /** 현재 차수 정보 (단일차수 포함) */
  currentRound: MissionRound | null
  /** 현재 차수의 제출 상태 */
  currentSubmissionStatus: RoundSubmissionStatus | null
  /** 다차수 여부 */
  isMultiRound: boolean
  forceMobile: boolean
}

export function MissionGuideSection({
  campaign,
  currentRound,
  currentSubmissionStatus,
  isMultiRound,
  forceMobile,
}: MissionGuideSectionProps) {
  // 가이드 접힘/펼침 (모바일)
  const [guideExpanded, setGuideExpanded] = useState(false)
  const guideContentRef = useRef<HTMLDivElement>(null)
  const [guideOverflows, setGuideOverflows] = useState(false)

  // 가이드 콘텐츠 높이 측정 → 150px 초과 시 접힘 활성화
  useEffect(() => {
    const el = guideContentRef.current
    if (!el) {
      setGuideOverflows(false)
      return
    }
    setGuideOverflows(el.scrollHeight > 150)
    setGuideExpanded(false)
  }, [campaign, currentRound])

  const effectiveGuideline = currentRound?.guideline

  return (
    <div className="py-6">
      <div className={forceMobile ? '' : 'md:sticky md:top-6'}>
        <h3 className="text-base font-semibold text-on-surface mb-1">
          {isMultiRound && currentRound?.title
            ? `${currentRound.title} 미션 가이드`
            : '미션 가이드'}
        </h3>

        {/* 마감일 표시 */}
        {(() => {
          const deadlineStr = isMultiRound && currentRound
            ? currentRound.deadline_at
            : campaign?.mission_deadline_at
          if (!deadlineStr) return null
          const deadline = new Date(deadlineStr)
          const now = new Date()
          const diffMs = deadline.getTime() - now.getTime()
          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
          const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
          const remaining = diffMs > 0
            ? diffDays > 0 ? `${diffDays}일 ${diffHours}시간 남음` : `${diffHours}시간 남음`
            : '마감됨'
          const isOverdue = diffMs <= 0
          return (
            <div className="mb-4">
              <p className="text-sm text-on-surface-variant">
                마감일: {formatDate(deadlineStr)}
                <span className="ml-1.5">({remaining})</span>
              </p>
              {/* 기한 초과 + 미제출 시 경고 */}
              {isOverdue && currentSubmissionStatus === 'not_submitted' && (
                <p className="text-sm text-error font-medium mt-1">
                  제출 기한이 지났습니다. 빠른 제출을 부탁드립니다.
                </p>
              )}
            </div>
          )
        })()}

        {/* 가이드라인 첨부파일 — 상단 배치 */}
        {currentRound?.guideline_files && currentRound.guideline_files.length > 0 && (
          <div className="mb-4 space-y-1.5">
            {currentRound.guideline_files.map((url, i) => (
              <a
                key={i}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-surface rounded-lg px-3 py-2.5 text-sm text-on-surface hover:bg-surface-low transition-colors"
              >
                <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="truncate flex-1">{extractFilename(url)}</span>
                <span className="text-xs text-primary flex-shrink-0">다운로드</span>
              </a>
            ))}
          </div>
        )}

        {/* 가이드 텍스트 (있을 때만 표시, 모바일 접힘 처리) */}
        {effectiveGuideline && (
          <div>
            <div
              className={`relative overflow-hidden ${forceMobile ? '' : 'md:!max-h-none'}`}
              style={!guideExpanded && guideOverflows ? { maxHeight: 150 } : undefined}
            >
              <div ref={guideContentRef} className="text-base text-on-surface-variant whitespace-pre-line">
                <Linkify>{effectiveGuideline}</Linkify>
              </div>
              {/* 그라데이션 오버레이 */}
              {!guideExpanded && guideOverflows && (
                <div className={`absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent ${forceMobile ? '' : 'md:hidden'}`} />
              )}
            </div>
            {guideOverflows && (
              <button
                type="button"
                onClick={() => setGuideExpanded(!guideExpanded)}
                className={`w-full py-3 text-sm text-on-surface-variant hover:text-on-surface flex items-center justify-center gap-1 border-b border-border ${forceMobile ? '' : 'md:hidden'}`}
              >
                {guideExpanded ? '가이드 접기' : '가이드 더보기'}
                <svg className={`w-4 h-4 transition-transform ${guideExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
        )}

        <p className="text-xs text-outline-variant mt-3">
          ※ 미션 가이드에 맞지 않는 콘텐츠는 수정 요청이 있을 수 있습니다.
        </p>
      </div>
    </div>
  )
}
